/**
 * File Service - Handles file uploads and downloads using presigned URLs
 *
 * This service integrates with the backend tenant file controller that uses
 * presigned URLs for secure file uploads and downloads.
 *
 * Upload Flow:
 * 1. Request presigned URL from backend (POST /file/get-upload-url)
 * 2. Upload file directly to S3 using the presigned URL
 * 3. Confirm upload to backend (POST /file/confirm)
 *
 * Download Flow:
 * 1. Request presigned download URL (GET /file/:fileId/download-url)
 * 2. Use the URL to download/display the file
 */

import { API_URL, httpClient } from '../httpClient';

// ============================================
// TYPES
// ============================================

export type S3ACL = 'private' | 'public-read';

export interface FileUploadRequest {
  filename: string;
  acl?: S3ACL;
}

export interface FileUploadUrlResponse {
  uploadUrl: string;
  storageKey: string;
}

export interface FileConfirmResponse {
  id: number;
}

export interface FileDownloadUrlResponse {
  downloadUrl: string;
}

export interface UploadedFile {
  id: number;
  storageKey: string;
  filename: string;
}

export interface FileUploadProgress {
  loaded: number;
  total: number;
  percent: number;
}

// ============================================
// FILE API
// ============================================

export const fileApi = {
  /**
   * Get a presigned URL for uploading a file
   */
  getUploadUrl: async (request: FileUploadRequest): Promise<FileUploadUrlResponse> => {
    const { json } = await httpClient(`${API_URL}/file/get-upload-url`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
    return (json as { data: FileUploadUrlResponse }).data;
  },

  /**
   * Confirm that a file has been uploaded successfully
   */
  confirmUpload: async (storageKey: string): Promise<FileConfirmResponse> => {
    const { json } = await httpClient(`${API_URL}/file/confirm`, {
      method: 'POST',
      body: JSON.stringify({ storageKey }),
    });
    return (json as { data: FileConfirmResponse }).data;
  },

  /**
   * Get a presigned URL for downloading a file
   */
  getDownloadUrl: async (fileId: number, expiresIn?: number): Promise<string> => {
    const params = new URLSearchParams();
    if (expiresIn) {
      params.append('expiresIn', String(expiresIn));
    }
    const queryString = params.toString();
    const url = `${API_URL}/file/${fileId}/download-url${queryString ? `?${queryString}` : ''}`;

    const { json } = await httpClient(url, { method: 'GET' });
    return (json as { data: FileDownloadUrlResponse }).data.downloadUrl;
  },

  /**
   * Upload a file directly to S3 using a presigned URL
   * Returns upload progress via callback
   */
  uploadToS3: async (
    uploadUrl: string,
    file: File,
    onProgress?: (progress: FileUploadProgress) => void
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          onProgress({
            loaded: event.loaded,
            total: event.total,
            percent: Math.round((event.loaded / event.total) * 100),
          });
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload aborted'));
      });

      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
      xhr.send(file);
    });
  },

  /**
   * Complete file upload flow:
   * 1. Get presigned URL
   * 2. Upload to S3
   * 3. Confirm upload
   *
   * @returns The confirmed file with its ID and storage key
   */
  uploadFile: async (
    file: File,
    options?: {
      acl?: S3ACL;
      onProgress?: (progress: FileUploadProgress) => void;
    }
  ): Promise<UploadedFile> => {
    // Step 1: Get presigned URL
    const { uploadUrl, storageKey } = await fileApi.getUploadUrl({
      filename: file.name,
      acl: options?.acl ?? 'private',
    });

    // Step 2: Upload to S3
    await fileApi.uploadToS3(uploadUrl, file, options?.onProgress);

    // Step 3: Confirm upload
    const { id } = await fileApi.confirmUpload(storageKey);

    return {
      id,
      storageKey,
      filename: file.name,
    };
  },
};

// ============================================
// CUSTOM DATA PROVIDER METHODS
// ============================================

export const fileDataProviderMethods = {
  fileGetUploadUrl: fileApi.getUploadUrl,
  fileConfirmUpload: fileApi.confirmUpload,
  fileGetDownloadUrl: fileApi.getDownloadUrl,
  fileUpload: fileApi.uploadFile,
};

export default fileApi;
